import { openDB, type IDBPDatabase, type DBSchema } from 'idb'
import {
  ENTRY_INTAKE_INITIAL_STATE,
  parseEntryIntakeStateFromObject,
  parseEntryIntakeStateFromString,
  type EntryIntakeState,
} from '@/hooks/entry-intake-state'
import { ENTRY_INTAKE_UPDATE_EVENT } from '@/hooks/entry-intake-events'

type EntryIntakeRecord = {
  showId: string
  state: EntryIntakeState
  updatedAt: number
}

interface EntryIntakeDb extends DBSchema {
  states: { key: string; value: EntryIntakeRecord }
}

const DB_NAME = 'honeyshow-entry-intake'
const DB_VERSION = 1
const LEGACY_STORAGE_PREFIX = 'entry-intake-state-'

let dbPromise: Promise<IDBPDatabase<EntryIntakeDb>> | null = null

function getLegacyStorageKey(showId: string) {
  return `${LEGACY_STORAGE_PREFIX}${showId}`
}

function getDb(): Promise<IDBPDatabase<EntryIntakeDb>> {
  if (!dbPromise) {
    dbPromise = openDB<EntryIntakeDb>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('states')) {
          db.createObjectStore('states', { keyPath: 'showId' })
        }
      },
    })
  }
  return dbPromise
}

function dispatchUpdate(showId: string) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent(ENTRY_INTAKE_UPDATE_EVENT, {
      detail: { showId },
    })
  )
}

let broadcastChannel: BroadcastChannel | null = null

function handleBroadcastMessage(event: MessageEvent) {
  const showId = typeof event.data?.showId === 'string' ? event.data.showId : null
  if (showId) {
    dispatchUpdate(showId)
  }
}

function ensureBroadcastChannel() {
  if (broadcastChannel) return broadcastChannel
  if (typeof window === 'undefined' || !('BroadcastChannel' in window)) {
    return null
  }
  broadcastChannel = new BroadcastChannel('honeyshow-entry-intake')
  broadcastChannel.addEventListener('message', handleBroadcastMessage)
  return broadcastChannel
}

ensureBroadcastChannel()

function importLegacy(showId: string): EntryIntakeState {
  try {
    return parseEntryIntakeStateFromString(window.localStorage.getItem(getLegacyStorageKey(showId)))
  } catch {
    return structuredClone(ENTRY_INTAKE_INITIAL_STATE)
  }
}

function normalizeDurableState(value: unknown): EntryIntakeState {
  const state = parseEntryIntakeStateFromObject(value)
  state.pendingEntries = state.pendingEntries.map(entry => ({
    ...entry,
    submissionId: entry.submissionId ?? crypto.randomUUID(),
    editVersion: entry.editVersion ?? 0,
  }))
  const retired = new Set([
    ...(state.retiredCodes ?? []),
    ...state.pendingEntries.map(entry => entry.code),
    ...state.recentEntries.map(entry => entry.entryCode),
    ...state.checkedInExhibits.map(entry => entry.entryCode),
    ...(state.lastClaimedCanonical ? [state.lastClaimedCanonical] : []),
  ])
  state.retiredCodes = [...retired]
  state.availableCodes = [...new Set(state.availableCodes)].filter(code => !retired.has(code))
  return state
}

export async function mutateEntryIntakeState(
  showId: string,
  updater: (current: EntryIntakeState) => EntryIntakeState
): Promise<EntryIntakeState> {
  const db = await getDb()
  // IndexedDB serializes read/write transactions across connections and tabs.
  // Read and change the authoritative record within that same transaction.
  const tx = db.transaction('states', 'readwrite')
  try {
    const record = await tx.store.get(showId)
    const current = normalizeDurableState(record?.state ?? importLegacy(showId))
    const next = normalizeDurableState(updater(current))
    next.revision = (current.revision ?? 0) + 1
    await tx.store.put({ showId, state: next, updatedAt: Date.now() })
    await tx.done
    try {
      window.localStorage.removeItem(getLegacyStorageKey(showId))
    } catch {
      /* legacy cache only */
    }
    dispatchUpdate(showId)
    ensureBroadcastChannel()?.postMessage({ showId, revision: next.revision })
    return next
  } catch (error) {
    try {
      tx.abort()
    } catch {
      /* already aborted or completed */
    }
    await tx.done.catch(() => undefined)
    throw error
  }
}

export async function loadEntryIntakeState(showId: string): Promise<EntryIntakeState> {
  const db = await getDb()
  const record = await db.get('states', showId)
  if (
    record?.state &&
    (record.state.revision ?? 0) > 0 &&
    record.state.pendingEntries.every(entry => entry.submissionId)
  ) {
    return parseEntryIntakeStateFromObject(record.state)
  }
  // Migrate missing records/legacy identities atomically, re-reading after the
  // write lock is granted so a concurrent tab's newer commit always wins.
  return mutateEntryIntakeState(showId, state => state)
}

export async function clearEntryIntakeState(showId: string) {
  return mutateEntryIntakeState(showId, state => ({
    ...structuredClone(ENTRY_INTAKE_INITIAL_STATE),
    retiredCodes: state.retiredCodes,
    tmpCounter: state.tmpCounter,
    identifierOrders: state.identifierOrders,
    assignmentContexts: state.assignmentContexts,
    localAssignments: state.localAssignments,
  }))
}

export async function resetEntryIntakeDbForTests() {
  if (typeof window === 'undefined') return
  if (dbPromise) {
    try {
      const db = await dbPromise
      db.close()
    } catch {
      // ignore
    }
  }
  dbPromise = null
  if (broadcastChannel) {
    broadcastChannel.removeEventListener('message', handleBroadcastMessage)
    broadcastChannel.close()
    broadcastChannel = null
  }
  await new Promise<void>(resolve => {
    const request = indexedDB.deleteDatabase(DB_NAME)
    request.onsuccess = () => resolve()
    request.onerror = () => resolve()
    request.onblocked = () => resolve()
  })
}

export type { EntryIntakeState }
